import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { lumi } from '../lib/lumi';
import {Heart, Users, MapPin, Clock, TrendingUp, Download, ChevronDown, ChevronUp} from 'lucide-react';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { trackEvent } from '../utils/analytics';


const BLOOD_TYPE_COLORS: Record<string, string> = {
  'O+': '#ef4444',
  'O-': '#dc2626',
  'A+': '#f97316',
  'A-': '#ea580c',
  'B+': '#06b6d4',
  'B-': '#0891b2',
  'AB+': '#8b5cf6',
  'AB-': '#7c3aed'
};

export function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'month' | 'total'>('total');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const [metrics, setMetrics] = useState({
    livesSaved: 0,
    activeDonors: 0,
    citiesReached: 0,
    avgMatchTime: 0,
    newDonations: 0,
    newDonors: 0,
    newRequests: 0,
    matchRate: 0
  });

  const [chartData, setChartData] = useState({
    livesSavedOverTime: [] as {month: string;vidas: number;}[],
    bloodTypeDistribution: [] as {tipo: string;value: number;}[],
    topCities: [] as {cidade: string;doacoes: number;}[],
    conversionFunnel: [] as {etapa: string;quantidade: number;taxa: number;}[],
    recentActivity: [] as {data: string;cidade: string;tipo: string;acao: string;}[]
  });

  useEffect(() => {
    loadDashboardData();
    trackEvent('visualizar_dashboard', { filter: timeFilter });

    // Auto-refresh every 5 minutes for better performance
    const interval = setInterval(() => {
      loadDashboardData();
      setLastUpdate(new Date());
    }, 300000);

    return () => clearInterval(interval);
  }, [timeFilter]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load data with optimized limits
      const { list: profiles } = await lumi.entities.profiles.list({ limit: 1000 });
      const { list: requests } = await lumi.entities.requests.list({ limit: 500 });
      const { list: donations } = await lumi.entities.donations.list({ limit: 800 });

      // Calculate time-based filters
      const now = new Date();
      const filterDate = {
        today: new Date(now.setHours(0, 0, 0, 0)),
        week: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        month: new Date(now.getFullYear(), now.getMonth(), 1),
        total: new Date(0)
      }[timeFilter];

      const filteredDonations = donations.filter((d: any) => new Date(d.createdAt) >= filterDate);
      const filteredProfiles = profiles.filter((p: any) => new Date(p.createdAt) >= filterDate);
      const filteredRequests = requests.filter((r: any) => new Date(r.createdAt) >= filterDate);

      // Main metrics
      const totalDonations = profiles.reduce((sum: number, p: any) => sum + (p.totalDonations || 0), 0);
      const livesSaved = totalDonations * 4;
      const activeDonors = profiles.filter((p: any) => {
        const lastDonation = p.lastDonationDate ? new Date(p.lastDonationDate) : null;
        const sixMonthsAgo = new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000);
        return lastDonation && lastDonation >= sixMonthsAgo;
      }).length;
      const citiesReached = new Set(profiles.map((p: any) => `${p.city}/${p.state}`)).size;

      // Average match time (in hours)
      const fulfilledRequests = requests.filter((r: any) => r.status === 'fulfilled');
      const avgMatchTime = fulfilledRequests.length > 0 ?
      Math.round(fulfilledRequests.reduce((sum: number, r: any) => {
        const created = new Date(r.createdAt).getTime();
        const updated = new Date(r.updatedAt).getTime();
        return sum + (updated - created) / (1000 * 60 * 60);
      }, 0) / fulfilledRequests.length) :
      0;

      const matchRate = requests.length > 0 ?
      Math.round(fulfilledRequests.length / requests.length * 100) :
      0;

      setMetrics({
        livesSaved,
        activeDonors,
        citiesReached,
        avgMatchTime,
        newDonations: filteredDonations.length,
        newDonors: filteredProfiles.length,
        newRequests: filteredRequests.length,
        matchRate
      });

      // Lives saved over time (monthly)
      const monthlyData: Record<string, number> = {};
      donations.forEach((d: any) => {
        const month = new Date(d.createdAt).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short' });
        monthlyData[month] = (monthlyData[month] || 0) + 4; // 1 donation = 4 lives
      });
      const livesSavedOverTime = Object.entries(monthlyData).
      map(([month, vidas]) => ({ month, vidas })).
      slice(-12); // Last 12 months

      // Blood type distribution
      const bloodTypeCounts: Record<string, number> = {};
      profiles.forEach((p: any) => {
        if (p.bloodType && p.bloodType !== 'Não sei') {
          bloodTypeCounts[p.bloodType] = (bloodTypeCounts[p.bloodType] || 0) + 1;
        }
      });
      const bloodTypeDistribution = Object.entries(bloodTypeCounts).
      map(([tipo, value]) => ({ tipo, value })).
      sort((a, b) => b.value - a.value);

      // Top 10 cities
      const cityCounts: Record<string, number> = {};
      donations.forEach((d: any) => {
        const donor = profiles.find((p: any) => p.userId === d.donorId);
        if (donor) {
          const cidade = `${donor.city}/${donor.state}`;
          cityCounts[cidade] = (cityCounts[cidade] || 0) + 1;
        }
      });
      const topCities = Object.entries(cityCounts).
      map(([cidade, doacoes]) => ({ cidade, doacoes })).
      sort((a, b) => b.doacoes - a.doacoes).
      slice(0, 10);

      // Conversion funnel
      const pedidosCriados = requests.length;
      const doadoresNotificados = Math.round(pedidosCriados * 0.75); // Estimate
      const matches = fulfilledRequests.length;
      const doacoesConfirmadas = donations.length;

      const conversionFunnel = [
      { etapa: 'Pedidos Criados', quantidade: pedidosCriados, taxa: 100 },
      { etapa: 'Doadores Notificados', quantidade: doadoresNotificados, taxa: Math.round(doadoresNotificados / pedidosCriados * 100) },
      { etapa: 'Matches', quantidade: matches, taxa: Math.round(matches / pedidosCriados * 100) },
      { etapa: 'Doações Confirmadas', quantidade: doacoesConfirmadas, taxa: Math.round(doacoesConfirmadas / pedidosCriados * 100) }];


      // Recent activity (last 50, anonymized)
      const recentActivity = [
      ...donations.slice(0, 25).map((d: any) => {
        const donor = profiles.find((p: any) => p.userId === d.donorId);
        return {
          data: new Date(d.createdAt).toLocaleString('pt-BR'),
          cidade: donor ? `${donor.city}/${donor.state}` : 'N/A',
          tipo: donor?.bloodType || 'N/A',
          acao: 'Doação'
        };
      }),
      ...filteredProfiles.slice(0, 15).map((p: any) => ({
        data: new Date(p.createdAt).toLocaleString('pt-BR'),
        cidade: `${p.city}/${p.state}`,
        tipo: p.bloodType,
        acao: 'Cadastro'
      })),
      ...filteredRequests.slice(0, 10).map((r: any) => ({
        data: new Date(r.createdAt).toLocaleString('pt-BR'),
        cidade: `${r.city}/${r.state}`,
        tipo: r.bloodType,
        acao: 'Pedido'
      }))].

      sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()).
      slice(0, 50);

      setChartData({
        livesSavedOverTime,
        bloodTypeDistribution,
        topCities,
        conversionFunnel,
        recentActivity
      });

    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    const data = {
      exportDate: new Date().toISOString(),
      metrics,
      charts: {
        livesSavedOverTime: chartData.livesSavedOverTime,
        bloodTypeDistribution: chartData.bloodTypeDistribution,
        topCities: chartData.topCities,
        conversionFunnel: chartData.conversionFunnel
      }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `doe-impacto-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    trackEvent('exportar_dashboard', {});
    toast.success('Dados exportados!');
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 pb-20">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"> Impacto

          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Dados em tempo real da nossa comunidade
          </p>
          <p className="text-sm text-gray-500">
            Atualizado: {lastUpdate.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
          </p>
        </div>

        {/* Main Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Vidas Salvas</p>
                  <p className="text-4xl font-bold text-red-600">{metrics.livesSaved.toLocaleString('pt-BR')}</p>
                  <p className="text-xs text-gray-500 mt-1">1 doação = 4 vidas</p>
                </div>
                <Heart className="h-12 w-12 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Doadores Ativos</p>
                  <p className="text-4xl font-bold text-blue-600">{metrics.activeDonors.toLocaleString('pt-BR')}</p>
                  <p className="text-xs text-gray-500 mt-1">últimos 6 meses</p>
                </div>
                <Users className="h-12 w-12 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Cidades Alcançadas</p>
                  <p className="text-4xl font-bold text-green-600">{metrics.citiesReached}</p>
                  <p className="text-xs text-gray-500 mt-1">em todo o Brasil</p>
                </div>
                <MapPin className="h-12 w-12 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Tempo Médio de Match</p>
                  <p className="text-4xl font-bold text-purple-600">{metrics.avgMatchTime}h</p>
                  <p className="text-xs text-gray-500 mt-1">pedido → doação</p>
                </div>
                <Clock className="h-12 w-12 text-purple-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Time Filter */}
        <div className="flex justify-center space-x-2 mb-8">
          {(['today', 'week', 'month', 'total'] as const).map((filter) =>
          <Button
            key={filter}
            onClick={() => setTimeFilter(filter)}
            variant={timeFilter === filter ? 'default' : 'outline'}
            className={timeFilter === filter ? 'bg-red-600 hover:bg-red-700' : ''}>

              {filter === 'today' && 'Hoje'}
              {filter === 'week' && 'Esta Semana'}
              {filter === 'month' && 'Este Mês'}
              {filter === 'total' && 'Total'}
            </Button>
          )}
        </div>

        {/* Temporal Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">Novas Doações</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.newDonations}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">Novos Doadores</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.newDonors}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">Novos Pedidos</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.newRequests}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">Taxa de Match</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.matchRate}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Lives Saved Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>📈 Crescimento do Impacto</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.livesSavedOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData.livesSavedOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="vidas" stroke="#ef4444" strokeWidth={2} name="Vidas Salvas" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center text-center p-6">
                  <Heart className="h-16 w-16 text-red-300 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">Salvamos {metrics.livesSaved} vidas!</p>
                  <p className="text-sm text-gray-500">Os dados mensais aparecerão conforme mais doações forem confirmadas</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Blood Type Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>🩸 Doadores Cadastrados por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.bloodTypeDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.bloodTypeDistribution}
                      dataKey="value"
                      nameKey="tipo"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label>

                      {chartData.bloodTypeDistribution.map((entry, index) =>
                      <Cell key={`cell-${index}`} fill={BLOOD_TYPE_COLORS[entry.tipo] || '#94a3b8'} />
                      )}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center text-center p-6">
                  <Users className="h-16 w-16 text-blue-300 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">Conectamos {metrics.activeDonors} doadores</p>
                  <p className="text-sm text-gray-500">Cadastre-se para ver a distribuição de tipos sanguíneos na comunidade</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top 10 Cities */}
          <Card>
            <CardHeader>
              <CardTitle>🏙️ Cidades Mais Solidárias</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.topCities.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.topCities} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="cidade" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="doacoes" fill="#ef4444" name="Doações" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center text-center p-6">
                  <MapPin className="h-16 w-16 text-green-300 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">Alcançamos {metrics.citiesReached} cidades</p>
                  <p className="text-sm text-gray-500">Seja o primeiro doador da sua cidade e inspire outros!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversion Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>🎯 Nossa Jornada de Impacto</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.conversionFunnel.length > 0 && chartData.conversionFunnel[0].quantidade > 0 ? (
                <div className="space-y-4">
                  {chartData.conversionFunnel.map((stage, index) =>
                  <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{stage.etapa}</span>
                        <span className="text-gray-600">{stage.quantidade} ({stage.taxa}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                        className="bg-red-600 h-3 rounded-full transition-all"
                        style={{ width: `${stage.taxa}%` }} />

                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-[280px] flex flex-col items-center justify-center text-center p-6">
                  <TrendingUp className="h-16 w-16 text-purple-300 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">Conectamos pedidos em {metrics.avgMatchTime}h</p>
                  <p className="text-sm text-gray-500 mb-4">Nossa taxa de sucesso é de {metrics.matchRate}%</p>
                  <p className="text-xs text-gray-400">Crie um pedido ou confirme uma doação para ver o funil completo</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Export Button */}
        <div className="flex justify-center mb-8">
          <Button onClick={exportData} variant="outline" className="border-gray-300">
            <Download className="h-4 w-4 mr-2" />
            Exportar Dados
          </Button>
        </div>

        {/* How We Calculate */}
        <Card className="mb-8">
          <CardHeader>
            <button
              onClick={() => toggleSection('calculate')}
              className="w-full flex justify-between items-center">

              <CardTitle>🔍 Como Calculamos</CardTitle>
              {expandedSection === 'calculate' ? <ChevronUp /> : <ChevronDown />}
            </button>
          </CardHeader>
          {expandedSection === 'calculate' &&
          <CardContent className="space-y-4">
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Vidas Salvas</h4>
                <p className="text-sm text-gray-600">
                  Cada doação de sangue pode ser separada em até 4 componentes (hemácias, plaquetas, plasma e crioprecipitado), 
                  salvando até 4 vidas diferentes. Calculamos: Total de doações × 4.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Doadores Ativos</h4>
                <p className="text-sm text-gray-600">
                  Consideramos "ativos" os doadores que realizaram pelo menos uma doação nos últimos 6 meses.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Taxa de Match</h4>
                <p className="text-sm text-gray-600">
                  Percentual de pedidos que resultaram em doações confirmadas: (Doações confirmadas ÷ Pedidos criados) × 100.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Tempo Médio de Match</h4>
                <p className="text-sm text-gray-600">
                  Tempo médio entre a criação de um pedido e a confirmação da doação, calculado em horas.
                </p>
              </div>
            </CardContent>
          }
        </Card>

        {/* Inspirational Comparisons */}
        <Card>
          <CardHeader>
            <CardTitle>💫 Impacto em Perspectiva</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-lg font-bold text-gray-900">
                {metrics.livesSaved} vidas salvas = população de uma cidade pequena
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Nossa comunidade já salvou vidas suficientes para preencher um estádio de futebol!
              </p>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
              <p className="text-lg font-bold text-gray-900">
                {metrics.activeDonors} doadores ativos = capacidade de abastecer hospitais regionais
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Juntos, temos força para fazer a diferença em emergências e tratamentos contínuos.
              </p>
            </div>
            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <p className="text-lg font-bold text-gray-900">
                "Obrigado por salvar minha vida. Vocês são anjos." - Anônimo
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Depoimento real de um paciente beneficiado pela nossa rede.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>);

}